import { DashboardAmountCard } from 'pages/Dashboard/components/DashboardAmountCard/DashboardAmountCard';
import { DashboardCard } from 'pages/Dashboard/components/DashboardCard/DashboardCard';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { formatCurrency } from 'utils/formatCurrency';

import { BoxStyled } from './DashboardTransactionsSummary.styled';
import { DashboardTransactionsSummaryChart } from './DashboardTransactionsSummaryChart';
import { DashboardTransactionsSummarySkeleton } from './DashboardTransactionsSummarySkeleton';
import { GetDashboardTransactionsSuccessSummaryResult } from './hooks/useGetDashboardTransactionsSuccessSummary';
import { GetDashboardTransactionsSummaryResult } from './hooks/useGetDashboardTransactionsSummary';

const formatSummaryAmount = (amount?: string, currency?: string) =>
  `${formatCurrency(Number(amount) || 0)} ${currency || ''}`;

type Props = {
  transactionsSummary: GetDashboardTransactionsSummaryResult;
  transactionsSuccessSummary: GetDashboardTransactionsSuccessSummaryResult;
  summaryPeriod: string;
};

export const DashboardTransactionsSummary: React.FC<Props> = ({
  transactionsSummary,
  transactionsSuccessSummary,
  summaryPeriod,
}) => {
  const translate = useTypedTranslate();

  const isFetchingAggregated =
    transactionsSuccessSummary.isFetching || transactionsSummary.isFetching;

  const error = transactionsSuccessSummary.error || transactionsSummary.error;

  if (isFetchingAggregated) {
    return <DashboardTransactionsSummarySkeleton />;
  }

  const { convertedPaymentAmount, convertedPayoutAmount, currency } =
    transactionsSummary.summary || {};
  const { total, successful, refused, pending } = transactionsSuccessSummary.summary || {};

  return (
    <DashboardCard
      cardHeaderProps={{
        description: summaryPeriod,
        title: translate('app.dashboard.transactionsSummary.title'),
      }}
    >
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box>
          <BoxStyled>
            <DashboardAmountCard
              color="success.main"
              text={formatSummaryAmount(convertedPaymentAmount, currency)}
              title={translate('app.dashboard.transactionsSummary.deposits')}
            />
            <DashboardAmountCard
              color="primary.main"
              text={formatSummaryAmount(convertedPayoutAmount, currency)}
              title={translate('app.dashboard.transactionsSummary.withdrawals')}
            />
          </BoxStyled>
          <DashboardTransactionsSummaryChart summary={transactionsSuccessSummary.summary} />
          <BoxStyled>
            <DashboardAmountCard
              text={`${total || '0'}`}
              title={translate('app.dashboard.successfulTransactionsSummary.total')}
            />
            <DashboardAmountCard
              color="success.main"
              text={`${successful?.count || '0'} (${successful?.percentage || 0}%)`}
              title={translate('app.dashboard.successfulTransactionsSummary.successful')}
            />
            <DashboardAmountCard
              color="error.main"
              text={`${refused?.count || '0'} (${refused?.percentage || 0}%)`}
              title={translate('app.dashboard.successfulTransactionsSummary.refused')}
            />
            <DashboardAmountCard
              color="warning.light"
              text={`${pending?.count || '0'} (${pending?.percentage || 0}%)`}
              title={translate('app.dashboard.successfulTransactionsSummary.pending')}
            />
          </BoxStyled>
        </Box>
      )}
    </DashboardCard>
  );
};
